import { Card, Typography, Button, Box }                 from '@mui/material';
import { useGlobalConfig }                               from 'context/GlobalContext';
import React, { useState }                               from 'react';
import { useSelector }                                   from 'react-redux';
import { selectCMSSelectedPilot
       }                                                 from 'store/dataReducers/cmsPilotSlice';
import { GetValues }                                     from 'types/cms/frontendTypes';
import * as XLSX                                         from 'xlsx';
import { RecoApis }                                      from 'apis/recoAPI';
import { BglPrimaryButton }                              from 'components/button/BglButtons';
import CloudUploadIcon                                   from '@mui/icons-material/CloudUpload';

const categoryMap = {
  'All' : 'totalEnergy',
  'Total Energy' : 'totalEnergy',
  'Whole Premise' : 'totalEnergy',
  'Whole Home' : 'totalEnergy',
  'Air Conditioning' : 'cooling',
  'Always On' : 'alwaysOn',
  'Cooking' : 'cooking',
  'Entertainment and computing' : 'entertainmentComputing',
  'EV' : 'electricVehicle',
  'Garden Irrigation' : 'irrigation',
  'Heating' : 'spaceHeating',
  'Laundry' : 'laundry',
  'Lighting' : 'lighting',
  'Pool' : 'pool',
  'Pools and saunas' : 'poolSauna',
  'Refrigeration - Freezer' : 'refrigeration',
  'Showers & Baths' : 'showerBath',
  'Toilets & Flushes' : 'toiletsFlushes',
  'Washing Appliances' : 'washingAppliances',
  'Water Heating' : 'waterHeating',
  'HVAC' : 'hvac',
  'Others' : 'others',
  'Solar' : 'solar'
} as any

const ExcelParser: React.FC = () => {

  const { rc } = useGlobalConfig()
  
  const [recoFileData, setRecoFileData] = useState<{fileName : string, recoModelJson : any, forCustomerReviewJson : any}>()
  const [recoScoreFileData, setRecoScoreFileData] = useState<{fileName : string, recoScoreValues : any[]}>()

  const [errors, setErrors] = useState<{recoId : string, error : string}[]>([])

  const selectedPilot = useSelector(selectCMSSelectedPilot)!
  
  const handleRecoFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseRecoFile(file)
    }
  };

  const handleRecoScoreFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      parseRecoScoreFile(file)
    }
  }

  const parseRecoFile = (file: File) => {

    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      
      const recoModel = workbook.Sheets['RECO_MODEL'];
      const recoModelJson = XLSX.utils.sheet_to_json(recoModel, { header : 'A'});

      const forCustomerReview = workbook.Sheets['FOR_CUSTOMER_REVIEW'];
      const forCustomerReviewJson = XLSX.utils.sheet_to_json(forCustomerReview, { header : 'A'});

      setRecoFileData({fileName : file.name, recoModelJson, forCustomerReviewJson})      
    };
    reader.readAsArrayBuffer(file);
  }

  const parseRecoScoreFile = (file: File) => {

    const reader = new FileReader();

    reader.onload = async (event) => {
      const data = new Uint8Array(event.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });

      const recoScore = workbook.Sheets['Standard RESI Reco Sheet 2024.1']
      const recoScoreJson = XLSX.utils.sheet_to_json(recoScore, { header : 'A'})

      setRecoScoreFileData({fileName : file.name, recoScoreValues : recoScoreJson})
    }

    reader.readAsArrayBuffer(file);
  }

  const syncToServer = async () => {

    if (!recoFileData || !recoScoreFileData) {
      setErrors([{recoId : '', error : 'Please upload both reco and reco score files'}])
      return
    }
    
    const {recoModelJson, forCustomerReviewJson} = recoFileData
    const {recoScoreValues} = recoScoreFileData

    const recos = []

    for (let i = 9; i < recoModelJson.length; i++) {

      const recoModelRow = recoModelJson[i] as any
      const recoId = recoModelRow['C']

      if (!recoId) break

      const contentRow = forCustomerReviewJson.filter((row : any) => row['A'] === recoId)[0] as any

      if (!contentRow) {
        throw Error(`Reco Id ${recoId} does not exsit in FOR_CUSTOMER_REVIEW sheet`)
      }

      const accountTypeResi = contentRow['AC'] === 'Y'
      let category = recoModelRow['D']
      if (!categoryMap[category]) throw Error(`Category ${category} is not valid`)
      category = categoryMap[category]

      let fuelType = []
      if (contentRow['AE'] === 'Y') fuelType.push('ELECTRIC')
      if (contentRow['AF'] === 'Y') fuelType.push('GAS')
      if (contentRow['AG'] === 'Y') fuelType.push('WATER')

      let disabled = false;
      if (contentRow['AH'] === 'Yes') disabled = true

      let defaultReco = false;
      if (contentRow['AI'] === 'Yes') defaultReco = true

      let propertyType = []
      if (recoModelRow['R']) propertyType.push('detached');
      if (recoModelRow['S']) propertyType.push('semiDetached');
      if (recoModelRow['T']) propertyType.push('bungalow');
      if (recoModelRow['U']) propertyType.push('terraced');
      if (recoModelRow['V']) propertyType.push('endTerrace');
      if (recoModelRow['W']) propertyType.push('apartment');

      let businessType = []
      if (recoModelRow['X']) businessType.push('office');
      if (recoModelRow['Y']) businessType.push('restaurant');
      if (recoModelRow['Z']) businessType.push('healthcare');
      if (recoModelRow['AA']) businessType.push('lodging');
      if (recoModelRow['AB']) businessType.push('education');
      if (recoModelRow['AC']) businessType.push('fbRetail');
      if (recoModelRow['AD']) businessType.push('publicAssembly');
      if (recoModelRow['AE']) businessType.push('publicAdministration');
      if (recoModelRow['AF']) businessType.push('misc');
      if (recoModelRow['AG']) businessType.push('nonFbRetail');
      if (recoModelRow['AH']) businessType.push('nonRefrigeratedStorage');
      if (recoModelRow['AI']) businessType.push('refrigeratedStorage');
      if (recoModelRow['AJ']) businessType.push('itInfrastructure');
      if (recoModelRow['AK']) businessType.push('default');
      if (recoModelRow['AL']) businessType.push('otherServices');

      let propertySize = []
      if (recoModelRow['AM']) propertySize.push('1Bedroom');
      if (recoModelRow['AN']) propertySize.push('2Bedrooms');
      if (recoModelRow['AO']) propertySize.push('3Bedrooms');
      if (recoModelRow['AP']) propertySize.push('4+Bedrooms');

      let livingArea = []
      if (recoModelRow['AQ']) livingArea.push('0-1000');
      if (recoModelRow['AR']) livingArea.push('1000-2500');
      if (recoModelRow['AS']) livingArea.push('2500-5000');
      if (recoModelRow['AT']) livingArea.push('5000-10000');
      if (recoModelRow['AU']) livingArea.push('>10000');

      let propertyAge = []
      if (recoModelRow['AV']) propertyAge.push('pre-1919');
      if (recoModelRow['AW']) propertyAge.push('1920-1975');
      if (recoModelRow['AX']) propertyAge.push('1976-1999');
      if (recoModelRow['AY']) propertyAge.push('post-2000');
      
      let ownership = []
      if (recoModelRow['AZ']) ownership.push('owned');
      if (recoModelRow['BA']) ownership.push('rented');

      let occupants = []
      if (recoModelRow['BC']) occupants.push('1');
      if (recoModelRow['BD']) occupants.push('2');
      if (recoModelRow['BE']) occupants.push('3');
      if (recoModelRow['BF']) occupants.push('4');
      if (recoModelRow['BG']) occupants.push('5+');

      let employees = []
      if (recoModelRow['BH']) employees.push('1-50');
      if (recoModelRow['BI']) employees.push('51-100');
      if (recoModelRow['BJ']) employees.push('101-250');
      if (recoModelRow['BK']) employees.push('>250');

      let occupantType = []
      if (recoModelRow['BL']) occupantType.push('students');
      if (recoModelRow['BM']) occupantType.push('workingAdults');
      if (recoModelRow['BN']) occupantType.push('atHomeAdults');
      if (recoModelRow['BO']) occupantType.push('youngFamily');
      if (recoModelRow['BP']) occupantType.push('olderFamily');
      if (recoModelRow['BQ']) occupantType.push('retirees');

      let yardGarden = []
      if (recoModelRow['BR']) yardGarden.push('yes');
      if (recoModelRow['BS']) yardGarden.push('no');

      let yardGardenSize = []
      if (recoModelRow['BT']) yardGardenSize.push('0m2');
      if (recoModelRow['BU']) yardGardenSize.push('0-50m2');
      if (recoModelRow['BV']) yardGardenSize.push('50-100m2');
      if (recoModelRow['BW']) yardGardenSize.push('100-200m2');
      if (recoModelRow['BX']) yardGardenSize.push('200-500m2');
      if (recoModelRow['BY']) yardGardenSize.push('>500m2');
      
      let postalCode = []
      if (recoModelRow['BB']) postalCode.push('standardCountryFormats')

      let weather = []
      if (recoModelRow['EF']) weather.push('jan');
      if (recoModelRow['EG']) weather.push('feb');
      if (recoModelRow['EH']) weather.push('mar');
      if (recoModelRow['EI']) weather.push('apr');
      if (recoModelRow['EJ']) weather.push('may');
      if (recoModelRow['EK']) weather.push('jun');
      if (recoModelRow['EL']) weather.push('jul');
      if (recoModelRow['EM']) weather.push('aug');
      if (recoModelRow['EN']) weather.push('sep');
      if (recoModelRow['EO']) weather.push('oct');
      if (recoModelRow['EP']) weather.push('nov');
      if (recoModelRow['EQ']) weather.push('dec');

      let tier = "NONE"
      if (recoModelRow['EV']) tier = 'YES';
      if (recoModelRow['EW']) tier = 'NO';

      let tou = "NONE"
      if (recoModelRow['EX']) tou = 'YES';
      if (recoModelRow['EY']) tou = 'NO';

      let ratePlanExclusion = {} as any
      ratePlanExclusion.tier = tier
      ratePlanExclusion.tou = tou
      ratePlanExclusion.flat = 'NONE'
      if (recoModelRow['EZ']) ratePlanExclusion.flat = 'YES'

      const applianceProfileExclusion = {} as Record<number, string[]>

      if (recoModelRow['BZ']) {
        applianceProfileExclusion['502'] = ['WATER']
        applianceProfileExclusion['505'] = ['WATER']
        applianceProfileExclusion['506'] = ['WATER']
        applianceProfileExclusion['507'] = ['WATER']
      }
      if (recoModelRow['CA']) {
        applianceProfileExclusion['508'] = ['WATER']
      }
      if (recoModelRow['CB']) {
        applianceProfileExclusion['501'] = ['WATER']
      }

      const appId3 = []
      if (recoModelRow['CC']) {
        appId3.push('GAS')
      }
      if (recoModelRow['CD']) {
        appId3.push('ELECTRIC')
      }
      if (recoModelRow['CE']) {
        appId3.push('NONE')
      }
      applianceProfileExclusion['3'] = appId3

      const appId7 = []
      if (recoModelRow['CF']) {
        appId7.push('GAS')
      }
      if (recoModelRow['CG']) {
        appId7.push('ELECTRIC')
      }
      if (recoModelRow['CH']) {
        appId7.push('NONE')
      }
      applianceProfileExclusion['7'] = appId7

      const appId16 = []
      if (recoModelRow['CI']) {
        appId16.push('ELECTRIC')
      }
      if (recoModelRow['CJ']) {
        appId16.push('NONE')
      }
      applianceProfileExclusion['16'] = appId16

      const appId9 = []
      const appId10 = []
      const appId58 = []
      if (recoModelRow['CK']) {
        appId9.push('2+')
        appId10.push('2+')
        appId58.push('2+')
      }
      if (recoModelRow['CL']) {
        appId9.push('1')
        appId10.push('1')
        appId58.push('1')
      }
      if (recoModelRow['CM']) {
        appId9.push('0')
        appId10.push('0')
        appId58.push('0')
      }
      applianceProfileExclusion['9'] = appId9
      applianceProfileExclusion['10'] = appId10
      applianceProfileExclusion['58'] = appId58

      const appId23 = []
      if (recoModelRow['CN']) {
        appId23.push('GAS')
      }
      if (recoModelRow['CO']) {
        appId23.push('ELECTRIC')
      }
      if (recoModelRow['CP']) {
        appId23.push('NONE')
      }
      applianceProfileExclusion['23'] = appId23

      const appId36 = []
      if (recoModelRow['CQ']) {
        appId36.push('GAS')
      }
      if (recoModelRow['CR']) {
        appId36.push('ELECTRIC')
      }
      if (recoModelRow['CS']) {
        appId36.push('NONE')
      }
      applianceProfileExclusion['36'] = appId36

      const appId22 = []
      if (recoModelRow['CT']) {
        appId22.push('GAS')
      }
      if (recoModelRow['CU']) {
        appId22.push('ELECTRIC')
      }
      if (recoModelRow['CV']) {
        appId22.push('NONE')
      }
      applianceProfileExclusion['22'] = appId22

      const appId25 = []
      if (recoModelRow['CW']) {
        appId25.push('ELECTRIC')
      }
      if (recoModelRow['CX']) {
        appId25.push('NONE')
      }
      applianceProfileExclusion['25'] = appId25

      const appId40 = []
      if (recoModelRow['CY']) {
        appId40.push('ELECTRIC')
      }
      if (recoModelRow['CZ']) {
        appId40.push('NONE')
      }
      applianceProfileExclusion['40'] = appId40

      const appId24 = []
      if (recoModelRow['DA']) {
        appId24.push('ELECTRIC')
      }
      if (recoModelRow['DB']) {
        appId24.push('NONE')
      }
      applianceProfileExclusion['24'] = appId24

      const appId31 = []
      if (recoModelRow['DC']) {
        appId31.push('ELECTRIC')
      }
      if (recoModelRow['DD']) {
        appId31.push('NONE')
      }
      applianceProfileExclusion['31'] = appId31

      const appId30 = []
      if (recoModelRow['DE']) {
        appId30.push('ELECTRIC')
      }
      if (recoModelRow['DF']) {
        appId30.push('NONE')
      }
      applianceProfileExclusion['30'] = appId30

      const appId6 = []
      if (recoModelRow['DG']) {
        appId6.push('GAS')
      }
      if (recoModelRow['DH']) {
        appId6.push('ELECTRIC')
      }
      if (recoModelRow['DI']) {
        appId6.push('NONE')
      }
      applianceProfileExclusion['6'] = appId6

      const appId63 = []
      if (recoModelRow['DJ']) {
        appId63.push('ELECTRIC')
      }
      if (recoModelRow['DK']) {
        appId63.push('NONE')
      }
      applianceProfileExclusion['63'] = appId63

      const appId29 = []
      if (recoModelRow['DL']) {
        appId29.push('ELECTRIC')
      }
      if (recoModelRow['DM']) {
        appId29.push('NONE')
      }
      applianceProfileExclusion['29'] = appId29

      const appId61 = []
      if (recoModelRow['DN']) {
        appId61.push('ELECTRIC')
      }
      if (recoModelRow['DO']) {
        appId61.push('NONE')
      }
      applianceProfileExclusion['61'] = appId61

      const appId60 = []
      if (recoModelRow['DP']) {
        appId60.push('ELECTRIC')
      }
      if (recoModelRow['DQ']) {
        appId60.push('NONE')
      }
      applianceProfileExclusion['60'] = appId60

      const appId65 = []
      if (recoModelRow['DR']) {
        appId65.push('ELECTRIC')
      }
      if (recoModelRow['DS']) {
        appId65.push('NONE')
      }
      applianceProfileExclusion['65'] = appId65

      const appId28 = []
      if (recoModelRow['DT']) {
        appId28.push('ELECTRIC')
      }
      if (recoModelRow['DU']) {
        appId28.push('NONE')
      }
      applianceProfileExclusion['28'] = appId28

      const appId64 = []
      if (recoModelRow['DV']) {
        appId64.push('ELECTRIC')
      }
      if (recoModelRow['DW']) {
        appId64.push('NONE')
      }
      applianceProfileExclusion['64'] = appId64

      const appId44 = []
      if (recoModelRow['DX']) {
        appId44.push('ELECTRIC')
      }
      if (recoModelRow['DY']) {
        appId44.push('NONE')
      }
      applianceProfileExclusion['44'] = appId44

      const appId18 = []
      if (recoModelRow['DZ']) {
        appId18.push('ELECTRIC')
      }
      if (recoModelRow['EA']) {
        appId18.push('NONE')
      }
      applianceProfileExclusion['18'] = appId18

      const appId2 = []
      if (recoModelRow['EB']) {
        appId2.push('ELECTRIC')
      }
      if (recoModelRow['EC']) {
        appId2.push('NONE')
      }
      applianceProfileExclusion['2'] = appId2

      const appId62 = []
      if (recoModelRow['ED']) {
        appId62.push('ELECTRIC')
      }
      if (recoModelRow['EE']) {
        appId62.push('NONE')
      }
      applianceProfileExclusion['62'] = appId62

      const appId8 = []
      if (recoModelRow['ER']) {
        appId8.push('ELECTRIC')
      }
      if (recoModelRow['ES']) {
        appId8.push('NONE')
      }
      applianceProfileExclusion['8'] = appId8

      const appId13 = []
      const appId52 = []
      if (recoModelRow['ET']) {
        appId13.push('ELECTRIC')
        appId52.push('ELECTRIC')
      }
      if (recoModelRow['EU']) {
        appId13.push('NONE')
        appId52.push('NONE')
      }
      applianceProfileExclusion['13'] = appId13
      applianceProfileExclusion['52'] = appId52

      const appId12 = []
      if (recoModelRow['EV']) {
        appId12.push('ELECTRIC')
      }
      if (recoModelRow['EW']) {
        appId12.push('NONE')
      }
      applianceProfileExclusion['12'] = appId12


      const insightAssociation = {
        NC : [],
        SC : [],
        CC : []
      } as Record<string, string[]>

      // TODO: Add the correct category names from categoryMap values
      if (recoModelRow['FG']) insightAssociation.NC.push('totalEnergy')
      if (recoModelRow['FH']) insightAssociation.SC.push('totalEnergy')
      if (recoModelRow['FI']) insightAssociation.CC.push('totalEnergy')

      if (recoModelRow['FJ']) insightAssociation.NC.push('cooling')
      if (recoModelRow['FK']) insightAssociation.SC.push('cooling')
      if (recoModelRow['FL']) insightAssociation.CC.push('cooling')

      if (recoModelRow['FM']) insightAssociation.NC.push('alwaysOn')
      if (recoModelRow['FN']) insightAssociation.SC.push('alwaysOn')
      if (recoModelRow['FO']) insightAssociation.CC.push('alwaysOn')

      if (recoModelRow['FP']) insightAssociation.NC.push('cooking')
      if (recoModelRow['FQ']) insightAssociation.SC.push('cooking')
      if (recoModelRow['FR']) insightAssociation.CC.push('cooking')

      if (recoModelRow['FS']) insightAssociation.NC.push('entertainmentComputing')
      if (recoModelRow['FT']) insightAssociation.SC.push('entertainmentComputing')
      if (recoModelRow['FU']) insightAssociation.CC.push('entertainmentComputing')

      if (recoModelRow['FV']) insightAssociation.NC.push('electricVehicle')
      if (recoModelRow['FW']) insightAssociation.SC.push('electricVehicle')
      if (recoModelRow['FX']) insightAssociation.CC.push('electricVehicle')

      if (recoModelRow['FY']) insightAssociation.NC.push('laundry')
      if (recoModelRow['FZ']) insightAssociation.SC.push('laundry')
      if (recoModelRow['GA']) insightAssociation.CC.push('laundry')

      if (recoModelRow['GB']) insightAssociation.NC.push('lighting')
      if (recoModelRow['GC']) insightAssociation.SC.push('lighting')
      if (recoModelRow['GD']) insightAssociation.CC.push('lighting')

      if (recoModelRow['GE']) insightAssociation.NC.push('poolSauna')
      if (recoModelRow['GF']) insightAssociation.SC.push('poolSauna')
      if (recoModelRow['GG']) insightAssociation.CC.push('poolSauna')

      if (recoModelRow['GH']) insightAssociation.NC.push('refrigeration')
      if (recoModelRow['GI']) insightAssociation.SC.push('refrigeration')
      if (recoModelRow['GJ']) insightAssociation.CC.push('refrigeration')

      if (recoModelRow['GK']) insightAssociation.NC.push('spaceHeating')
      if (recoModelRow['GL']) insightAssociation.SC.push('spaceHeating')
      if (recoModelRow['GM']) insightAssociation.CC.push('spaceHeating')

      if (recoModelRow['GN']) insightAssociation.NC.push('waterHeating')
      if (recoModelRow['GO']) insightAssociation.SC.push('waterHeating')
      if (recoModelRow['GP']) insightAssociation.CC.push('waterHeating')

      if (recoModelRow['GQ']) insightAssociation.NC.push('irrigation')
      if (recoModelRow['GR']) insightAssociation.SC.push('irrigation')
      if (recoModelRow['GS']) insightAssociation.CC.push('irrigation')

      if (recoModelRow['GT']) insightAssociation.NC.push('leakage')
      if (recoModelRow['GU']) insightAssociation.SC.push('leakage')
      if (recoModelRow['GV']) insightAssociation.CC.push('leakage')

      if (recoModelRow['GW']) insightAssociation.NC.push('others')
      if (recoModelRow['GX']) insightAssociation.SC.push('others')
      if (recoModelRow['GY']) insightAssociation.CC.push('others')

      if (recoModelRow['GZ']) insightAssociation.NC.push('pool')
      if (recoModelRow['HA']) insightAssociation.SC.push('pool')
      if (recoModelRow['HB']) insightAssociation.CC.push('pool')

      if (recoModelRow['HC']) insightAssociation.NC.push('showerBath')
      if (recoModelRow['HD']) insightAssociation.SC.push('showerBath')
      if (recoModelRow['HE']) insightAssociation.CC.push('showerBath')

      if (recoModelRow['HF']) insightAssociation.NC.push('toiletsFlushes')
      if (recoModelRow['HG']) insightAssociation.SC.push('toiletsFlushes')
      if (recoModelRow['HH']) insightAssociation.CC.push('toiletsFlushes')

      if (recoModelRow['HI']) insightAssociation.NC.push('washingAppliances')
      if (recoModelRow['HJ']) insightAssociation.SC.push('washingAppliances')
      if (recoModelRow['HK']) insightAssociation.CC.push('washingAppliances')

      const score = {
        impact : 1,
        simplicity : 1
      }
      const recoScore = recoScoreValues.find((row) => row['A'] === recoId)
      if (recoScore) {
        score.impact = recoScore['G']
        score.simplicity = recoScore['H']
      }

      const recoObj = {
        recommendationId : recoId,
        accountType : accountTypeResi ? 'RESIDENTIAL' : 'SMB',
        category : category,
        fuelType : fuelType,
        program : contentRow['M'] === 'Y',
        featuredContent : contentRow['L'] === 'Y',
        channelEmail : contentRow['U'] === 'Y',
        channelWeb : contentRow['U'] === 'Y',
        channelPaper : contentRow['T'] === 'Y',
        ctaButtonLink : contentRow['O'],
        ctaButtonText : contentRow['N'],
        title : contentRow['F'] || contentRow['E'],
        description : contentRow['I'] || contentRow['H'],
        detail : contentRow['W'] || contentRow['V'],
        videoLink : contentRow['X'],
        shortenedImage : contentRow['S'],
        fullImage : contentRow['Q'],
        elongatedImage : contentRow['R'],
        icon : contentRow['P'],
        locale : contentRow['D'] || contentRow['C'],
        defaultReco : defaultReco,
        disabled : disabled,
        homeProfileExclusion : {
          propertyType,
          businessType,
          propertySize,
          livingArea,
          propertyAge,
          ownership,
          occupants,
          employees,
          occupantType,
          yardGarden,
          yardGardenSize,
          weather,
          postalCode
        },
        ratePlanExclusion,
        applianceProfileExclusion: applianceProfileExclusion,
        ppExclusion: {},
        insightAssociation,
        score,
        editStatus : 'DRAFT',
        verificationStatus : 'TODO'
      } as GetValues<"api::recommendation.recommendation">

      recos.push(recoObj)
    }
    
    let batches = recos.length / 10
    if (recos.length % 10 !== 0) batches++

    const errors = []

    for (let i = 0; i < batches; i++) {
      const batch = recos.splice(0, 10)
      const resp = await RecoApis.ingestRecos(rc, selectedPilot.documentId, batch)
      if (resp.data?.error) {
        errors.push(...resp.data.error.map((error) => ({ recoId : error.documentId, error : error.error })))
      }
    }
    
    if (errors.length > 0) {
      setErrors(errors)
    }
  }

  const onSubmit = () => {    
    syncToServer()
  };

  return (
    <React.Fragment>
    <Card style={{ display: 'flex', flexDirection: 'column', padding: '24px'}}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, marginBottom: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ marginBottom: 1, fontWeight: 500 }}>
            Upload reco .xlsx file
          </Typography>
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
            sx={{ width: '100%', justifyContent: 'flex-start' }}
          >
            {recoFileData ? recoFileData.fileName : 'Choose file'}
            <input
              type="file"
              accept=".xlsx"
              onChange={handleRecoFileUpload}
              style={{ display: 'none' }}
            />
          </Button>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ marginBottom: 1, fontWeight: 500 }}> 
            Upload reco score .xlsx file 
          </Typography>
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
            sx={{ width: '100%', justifyContent: 'flex-start' }}
          >
            {recoScoreFileData ? recoScoreFileData.fileName : 'Choose file'}
            <input
              type="file"
              accept=".xlsx"
              onChange={handleRecoScoreFileUpload}
              style={{ display: 'none' }}
            />
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
        <BglPrimaryButton onClick={onSubmit}> 
          Upload
        </BglPrimaryButton>
      </Box>
    </Card>

    {errors.length > 0 && <Card sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', padding: '24px', overflowY: 'auto', maxHeight: '500px'}}>
      <Typography variant="body1" sx={{ textAlign: 'left', marginBottom: 1, fontWeight: 500 }}>
        {errors.map((error, index) => (
          <Typography key={index} variant="body1" sx={{ marginBottom: 1, fontWeight: 500 }}>
            {JSON.stringify(error)}
          </Typography>
        ))}
      </Typography>
    </Card>}
    </React.Fragment>
    
  );
};

export default ExcelParser;